import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Code2, ExternalLink, Loader2 } from 'lucide-react';
import { otcVerify, otcClient } from '@/lib/otcApi';
import { toast } from 'sonner';

async function otcCodeOpen(otc) {
  return (await otcClient.post(`/candidate-session/${otc}/code/open`)).data;
}

export default function CodeSession() {
  const { otc } = useParams();
  const { data } = useQuery({
    queryKey: ['otc-session-code', otc],
    queryFn: () => otcVerify(otc),
    retry: false,
  });
  const [opening, setOpening] = useState(false);
  const [openedUrl, setOpenedUrl] = useState(null);

  if (!data) return <Center icon={Loader2} spin title="Verifying your invitation…" />;
  const state = data.state;
  const stage = data.stage_code;
  if (state === 'invalid') return <Center icon={AlertTriangle} title="Invalid invitation" desc="The link is not recognised." />;
  if (state === 'expired') return <Center icon={AlertTriangle} title="Link expired" desc="Please request a fresh link from your recruiter." />;
  if (state === 'revoked') return <Center icon={AlertTriangle} title="Link revoked" desc="This invitation was withdrawn by the recruiter." />;
  if (state === 'consumed') {
    return <Center icon={CheckCircle2} emerald title="You&apos;ve already opened this challenge" desc="If you closed the editor tab by accident, please ask the recruiter to re-send the link." />;
  }
  if (stage && stage !== 'CODE') return <Center icon={AlertTriangle} title="Wrong link type" desc="This invitation is for a different assessment stage." />;
  if (!data.external_url) return <Center icon={AlertTriangle} title="Editor not configured" desc="The code editor URL has not been set up yet. Please ask the recruiter to contact the admin." />;

  const openChallenge = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const out = await otcCodeOpen(otc);
      setOpenedUrl(out.external_url);
      window.open(out.external_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message;
      toast.error(typeof d === 'string' ? d : 'Could not open the code challenge');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="hero-glow min-h-screen"><div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
      <Card className="card-elevated w-full"><CardContent className="p-8 space-y-5" data-testid="code-intro">
        <div className="text-center">
          <Code2 className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-2 text-2xl font-semibold">Code Challenge</h1>
          <p className="mt-1 text-sm text-muted-foreground">{data.candidate?.full_name}</p>
        </div>
        <div className="rounded-xl border bg-secondary/40 p-4 text-sm">
          <p>You&apos;ll be sent to an external hosted code editor in a new tab.</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>Complete the challenge as instructed in the editor.</li>
            <li>Your work is submitted directly through that editor &mdash; not through this page.</li>
            <li>Keep this tab open until you&apos;ve finished, in case you need to revisit the link.</li>
          </ul>
        </div>
        {openedUrl ? (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800">
            <p className="font-medium">Editor opened in a new tab.</p>
            <p className="mt-1">If it didn&apos;t open, <a href={openedUrl} target="_blank" rel="noreferrer" className="underline">click here</a>.</p>
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <Button onClick={openChallenge} disabled={opening} className="gap-1.5" data-testid="code-open-button">
            <ExternalLink className="h-4 w-4" /> {opening ? 'Opening…' : 'Open challenge'}
          </Button>
        </div>
      </CardContent></Card>
    </div></div>
  );
}

function Center({ icon: Icon, title, desc, spin = false, emerald = false }) {
  return (
    <div className="hero-glow min-h-screen"><div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
      <Card className="card-elevated"><CardContent className="p-8 text-center" data-testid="code-status-card">
        <Icon className={`mx-auto h-10 w-10 ${spin ? 'animate-spin' : ''} ${emerald ? 'text-emerald-500' : 'text-muted-foreground'}`} />
        <h1 className="mt-3 text-2xl font-semibold">{title}</h1>
        {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
      </CardContent></Card>
    </div></div>
  );
}
